import type { MeetingListItem } from "../types";

const MONTHS = [
  "янв", "фев", "мар", "апр", "мая", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export function fmtDateParts(iso: string) {
  const d = new Date(iso);
  return {
    day: String(d.getDate()),
    month: MONTHS[d.getMonth()],
    time: d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }),
    weekday: d.toLocaleDateString("ru-RU", { weekday: "short" }),
  };
}

export function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

export function fmtClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** Идёт ли встреча прямо сейчас (по расписанию или по факту старта). */
export function isLiveNow(m: MeetingListItem): boolean {
  if (m.status === "live") return true;
  if (m.status !== "scheduled") return false;
  const start = new Date(m.start_time).getTime();
  const end = start + m.duration_min * 60_000;
  const now = Date.now();
  return now >= start - 5 * 60_000 && now <= end;
}

export function statusBadge(m: MeetingListItem): {
  cls: string;
  label: string;
} {
  if (m.status === "canceled") return { cls: "red", label: "отменена" };
  if (m.status === "live") return { cls: "green", label: "идёт сейчас" };
  if (m.status === "ended") return { cls: "", label: "завершена" };
  if (isLiveNow(m)) return { cls: "green", label: "идёт сейчас" };
  return { cls: "blue", label: "запланирована" };
}

export function syncBadge(m: MeetingListItem): {
  cls: string;
  label: string;
  title: string;
} | null {
  switch (m.sync_status) {
    case "synced":
      return {
        cls: "green",
        label: `Zoom ${m.zoom_meeting_id ?? ""}`.trim(),
        title: "Встреча заведена в Zoom",
      };
    case "pending":
      return {
        cls: "amber",
        label: "ждёт Zoom",
        title: "Встреча в очереди на создание в Zoom",
      };
    case "error":
      return { cls: "red", label: "ошибка Zoom", title: m.sync_error };
    case "local":
      return {
        cls: "",
        label: "только локально",
        title: "Zoom не настроен — встреча существует только в сервисе",
      };
    default:
      return null;
  }
}

/** ISO UTC → строка для <input type="datetime-local"> в нужном часовом поясе. */
export function toNaiveLocal(iso: string, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(iso))) p[part.type] = part.value;
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

/** Ближайший «круглый» получас в будущем — удобный дефолт для новой встречи. */
export function defaultStart(timeZone: string): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return toNaiveLocal(d.toISOString(), timeZone);
}

export const TIMEZONES = [
  "Europe/Moscow",
  "Europe/Kaliningrad",
  "Europe/Samara",
  "Asia/Yekaterinburg",
  "Asia/Omsk",
  "Asia/Novosibirsk",
  "Asia/Krasnoyarsk",
  "Asia/Irkutsk",
  "Asia/Vladivostok",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Lisbon",
  "Asia/Almaty",
  "Asia/Tbilisi",
  "Asia/Dubai",
  "Asia/Yerevan",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];
